/**
 * TopNav V2.3-bis — barre de navigation horizontale supérieure fixe.
 *
 * Spec BULLE V2.3 :
 *   - Hauteur : 56 px (h-14)
 *   - Position : fixed top-0, bord gauche = largeur sidebar courante (animé)
 *   - Gauche : Breadcrumb automatique (depuis pathname)
 *   - Centre : contextSlot optionnel (contenu injecté par la page — filtres, etc.)
 *   - Droite : 3 boutons création RBAC + séparateur + NotificationsButton + SettingsButton + UserMenu
 *
 * V2.3-bis (T2) :
 *   - Boutons création : Importer (primary) · Activité (secondary) · FlashInfo (secondary)
 *   - RBAC filtré via hasPermission (useAuth)
 *   - Codes permission V1 : import.upload · activity.create · flashinfo.create
 *   - Séparateur visuel entre actions création et icônes globales
 *
 * V3.0 (PHASE 3.0 HOTFIX) : left fixe = WIDGET_SIDEBAR_W (150px).
 * Ancienne dépendance sidebarStore supprimée (Sidebar accordéon retirée).
 */

import { useT } from '@/lib/i18n';
import type { CleI18n } from '@i18n';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, BellPlus, FileCheck2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Breadcrumb } from './Breadcrumb';
import { LangToggle } from './LangToggle';
import { ModuleSelector } from './ModuleSelector';
import { SettingsButton } from './SettingsButton';
import { UserMenu } from './UserMenu';
import { SearchTrigger } from './SearchTrigger';
import { AlertBellCounter } from './AlertBellCounter';
import { FullscreenButton } from './FullscreenButton';
import { ROUTES } from '@/lib/routes';
import { WIDGET_SIDEBAR_W } from './WidgetSidebar';
import { fetchActiveBatch } from '@/modules/admin/import/lib/api';
import { usePendingBrandsCount } from '@/modules/admin/validation/hooks/usePendingBrandsCount';
import { usePendingSkusCount } from '@/modules/admin/validation/hooks/usePendingSkusCount';
import { usePendingPdvsCount } from '@/modules/admin/validation/hooks/usePendingPdvsCount';
import { usePendingMerchsCount } from '@/modules/admin/validation/hooks/usePendingMerchsCount';
import { useOrphanRawdataCount } from '@/modules/admin/validation/hooks/useOrphanRawdataCount';
import { useOrphanActivityCount } from '@/modules/admin/validation/hooks/useOrphanActivityCount';

// ─── Creation Actions ──────────────────────────────────────────────────────────

interface CreationAction {
  id: string;
  /** ⚠ `#839` — `label` est SUPPRIMÉ du type : les deux actions ont leur clé. */
  labelCle: CleI18n;
  icon: LucideIcon;
  path: string;
  /** Permission RBAC — string unique ou tableau OR (au moins 1 suffit). */
  permission: string | string[];
  variant: 'primary' | 'secondary';
}

/**
 * Boutons création globaux — spec NAV.9-bis T2.
 * Permissions : codes V1 exacts (seed.ts §2.x).
 */
const CREATION_ACTIONS: CreationAction[] = [
  {
    id: 'import',
    labelCle: 'nav.importer',
    icon: Upload,
    path: ROUTES.adminImport,
    permission: 'imports.gerer',
    variant: 'primary',
  },
  {
    id: 'flashinfo-create',
    // ★5 — le bouton disait « FlashInfo » (sans espace) en dur, quand le
    //   widget de la barre latérale était traduit depuis le `#798`.
    labelCle: 'nav.flashInfo',
    icon: BellPlus,
    path: ROUTES.flashinfoCreate,
    permission: 'flashinfo.gerer',
    variant: 'secondary',
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface TopNavProps {
  /**
   * Slot centre — contenu contextualisé par la page active (ex: DateRangePicker,
   * bouton filtre, badge de statut import…). Rendu seulement si fourni.
   */
  contextSlot?: ReactNode;
  /** Classe CSS additionnelle (rare — override cas spécifiques). */
  className?: string;
}

// ─── TopNav ───────────────────────────────────────────────────────────────────

/**
 * TopNav — header fixe décalé de la largeur sidebar.
 *
 * Note : `initial={false}` sur motion.header supprime l'animation d'entrée
 * (la position `left` est appliquée instantanément au mount, puis animée
 * uniquement sur les changements collapse/expand de la sidebar).
 */
export function TopNav({ contextSlot, className }: TopNavProps) {
  const t = useT();
  const totalLeft = WIDGET_SIDEBAR_W;
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  // Filtrage RBAC — OR logic supportée par hasPermission (string | string[])
  const visibleActions = CREATION_ACTIONS.filter((a) => hasPermission(a.permission));

  // Batch actif (APPROVED ou AWAITING_REVIEW) — pour le bouton Réconciliation
  const { data: activeBatch } = useQuery({
    queryKey: ['admin', 'active-batch'],
    queryFn:  fetchActiveBatch,
    staleTime: 60_000,
    retry: 1,
  });

  // BUG-RECON-BUTTON-UNDEFINED : guard strict sur l'id
  const batchId = activeBatch?.id && typeof activeBatch.id === 'string' ? activeBatch.id : undefined;

  // BUG-RECON-BUTTON-HIDDEN : visible pour tous les statuts actifs non-terminaux
  // (APPROVED_WITH_FORCING était manquant dans l'ancienne logique basée sur fetchHistory)
  const RECON_VISIBLE_STATUSES = [
    'AWAITING_REVIEW', 'APPROVED', 'APPROVED_WITH_FORCING',
    'UPLOADED_COMPLETE', 'UPLOADED_PARTIAL', 'RECONCILING',
  ];
  const showReconButton =
    hasPermission('imports.gerer') &&
    batchId != null &&
    RECON_VISIBLE_STATUSES.includes(activeBatch?.reconciliation_status ?? '');

  // Compteurs des 5 portes de réconciliation (pour couleur + badge)
  const { data: orphanRdData }  = useOrphanRawdataCount(batchId);
  const { data: orphanActData } = useOrphanActivityCount(batchId);
  const { data: brandsData }    = usePendingBrandsCount();
  const { data: skusData }      = usePendingSkusCount();
  const { data: pdvsData }      = usePendingPdvsCount();
  const { data: merchsData }    = usePendingMerchsCount();

  const orphanCount = (orphanRdData?.count ?? 0) + (orphanActData?.count ?? 0);
  const brandsCount = brandsData?.count ?? 0;
  const skusCount   = skusData?.count   ?? 0;
  const pdvsCount   = pdvsData?.count   ?? 0;
  const merchsCount = merchsData?.count ?? 0;
  const totalPending = orphanCount + brandsCount + skusCount + pdvsCount + merchsCount;
  const canPublish   = batchId != null && totalPending === 0;

  return (
    <motion.header
      initial={false}
      animate={{ left: totalLeft }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={cn(
        'fixed top-0 right-0 z-20',
        'flex h-14 items-center gap-3 px-4',
        'border-b border-surface-dark-border bg-surface-dark/95 backdrop-blur-sm',
        className
      )}
      aria-label={t('commun.barreNavigation')}
    >
      {/* ── Gauche : Breadcrumb ─────────────────────────────────────────── */}
      <div className="min-w-0 flex-1 flex items-center overflow-hidden">
        <Breadcrumb />
      </div>

      {/* ── Centre : contextSlot (optionnel) ────────────────────────────── */}
      {contextSlot != null && (
        <div className="flex shrink-0 items-center">
          {contextSlot}
        </div>
      )}

      {/* ── Droite : ModuleSelector + SearchTrigger + AlertBell + création + actions ─── */}
      <div className="flex shrink-0 items-center gap-2">

        {/* Sélecteur de module */}
        <ModuleSelector />

        {/* Recherche globale placeholder Cmd+K (réelle PHASE 3.2) */}
        <SearchTrigger />

        {/* Bell alertes avec count réel (PHASE 2 F1 réutilisé) */}
        <AlertBellCounter />

        {/* Séparateur */}
        <div className="h-5 w-px bg-[var(--border-default)]" aria-hidden="true" />

        {/* Boutons création RBAC + Publier — masqués si aucune permission */}
        {visibleActions.length > 0 && (
          <div className="flex items-center gap-1.5">
            {visibleActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => navigate(action.path)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-samsung-500/50',
                    action.variant === 'primary'
                      ? 'bg-brand-samsung-500 text-white hover:bg-brand-samsung-600 active:bg-brand-samsung-700'
                      : [
                          'border border-[var(--border-default)]',
                          'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                          'hover:bg-[var(--surface-hover)]',
                        ].join(' ')
                  )}
                >
                  <Icon size={13} className="shrink-0" aria-hidden="true" />
                  {t(action.labelCle)}
                </button>
              );
            })}

            {/* Bouton Réconciliation — BUG-RECON-BUTTON-HIDDEN : filtre statuts actifs */}
            {showReconButton && (
              <button
                type="button"
                onClick={() => navigate(ROUTES.adminImportReport(batchId))}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-samsung-500/50',
                  canPublish
                    ? 'bg-accent-success/90 text-white hover:bg-accent-success'
                    : 'bg-orange-500/90 text-white hover:bg-orange-500',
                )}
              >
                <FileCheck2 size={13} className="shrink-0" aria-hidden="true" />
                Réconciliation
                {totalPending > 0 && (
                  <span className="ml-0.5 rounded-full bg-white/20 px-1.5 py-0 text-[9px] font-bold leading-4 tabular-nums">
                    ⚠{totalPending}
                  </span>
                )}
              </button>
            )}
          </div>
        )}

        {/* Séparateur vertical — visible seulement si des actions création sont affichées */}
        {visibleActions.length > 0 && (
          <div
            className="h-5 w-px bg-[var(--border-default)]"
            aria-hidden="true"
          />
        )}

        {/* Actions globales (langue + fullscreen + settings + user menu) */}
        <div className="flex items-center gap-1">
          <LangToggle />
          <FullscreenButton />
          <SettingsButton />
          <UserMenu />
        </div>
      </div>
    </motion.header>
  );
}
